import clsx from "clsx";
import { type FC, useEffect, useMemo } from "react";

import { AddCourseGroupingModal } from "./AddCourseGroupingModal";
import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseNavListItem } from "./CourseNavListItem";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import {
  type SectionViewOption,
  SectionViewOptionMenu,
} from "components/menus/SectionViewOptionMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { AddPlusIcon, FolderIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsEditingAsOrgAdmin } from "store/selectors";
import {
  selectCourseGroupingsWithCourses,
  selectCoursesWithGrouping,
  selectUngroupedCourses,
} from "store/selectors/section";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsManager } from "utils/routing";

type CourseNavListProps = {
  onOptionChange: (option: SectionViewOption) => void;
};

export const CourseNavList: FC<CourseNavListProps> = ({ onOptionChange }) => {
  const groupingsWithCourses = useAppSelector(selectCourseGroupingsWithCourses);
  const ungroupedCourses = useAppSelector(selectUngroupedCourses);
  const coursesWithGrouping = useAppSelector(selectCoursesWithGrouping);
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const hasCourses = groupingsWithCourses.length > 0 || ungroupedCourses.length > 0;
  const courseId = getQueryParam(COURSE_ID_QUERY_PARAM);
  const groupingId = getQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const canEdit = useAppSelector(selectIsEditingAsOrgAdmin);
  const isSmallScreen = !useIsMdOrLarger();

  // Collect all course codes (from groupings and ungrouped) for finding current selection
  const allCourses = useMemo(
    () => [...groupingsWithCourses.flatMap((grouping) => grouping.courses), ...ungroupedCourses],
    [groupingsWithCourses, ungroupedCourses],
  );
  const currentGrouping = groupingsWithCourses.find((grouping) => grouping.id === groupingId);
  const currentCourse = allCourses.find((course) => course.id === courseId);
  const firstGroupId = groupingsWithCourses[0]?.id;
  const firstCourseId = ungroupedCourses[0]?.id;

  // Set default query params if neither grouping_id nor course_id are set
  useEffect(() => {
    if (!groupingId && !courseId) {
      if (firstGroupId) {
        // Set the first grouping if there are any groupings
        setQueryParams({
          [COURSE_GROUPING_ID_QUERY_PARAM]: firstGroupId,
          [COURSE_ID_QUERY_PARAM]: null,
        });
      } else if (firstCourseId) {
        // Set the first ungrouped course if there are no groupings
        setQueryParams({
          [COURSE_ID_QUERY_PARAM]: firstCourseId,
          [COURSE_GROUPING_ID_QUERY_PARAM]: null,
        });
      }
    }
  }, [groupingId, courseId, firstGroupId, firstCourseId, setQueryParams]);

  const handleCreateCourseGrouping = () => {
    showConfirmationModal({
      title: t("home.courses.add_course_grouping_heading"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseGroupingModal courses={coursesWithGrouping} onClose={onClose} />
      ),
    });
  };

  return (
    <div
      className={clsx(
        "grid max-h-[calc(100%-24px)] w-[240px] grid-rows-[auto_1fr_auto] rounded-2xl border border-black-tint-90 p-4",
        isSmallScreen && "w-full",
      )}
      role="list"
    >
      <div className="mb-4">
        <SectionViewOptionMenu
          eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
          selectedOption="course"
          onOptionChange={onOptionChange}
        />
      </div>

      <div className="min-h-0 overflow-y-auto">
        {/* Render groupings with their course codes */}
        {groupingsWithCourses.map((grouping) => {
          const isSelected = currentGrouping?.id === grouping.id;
          const isExpanded = isSelected || currentCourse?.grouping_id === grouping.id;

          return (
            <div key={grouping.id}>
              {/* Grouping header */}
              <div
                className="my-1 flex min-w-12 cursor-pointer items-center justify-between rounded-lg px-2 hover:bg-black-tint-97 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue data-[selected]:text-white"
                data-selected={isSelected || undefined}
                onClick={() =>
                  setQueryParams({
                    [COURSE_GROUPING_ID_QUERY_PARAM]: grouping.id,
                    [COURSE_ID_QUERY_PARAM]: null,
                  })
                }
              >
                <div className="flex items-center gap-2">
                  <FolderIcon />

                  <h3 className="body-s my-1 w-full min-w-12 truncate font-medium">
                    {grouping.title}
                  </h3>
                </div>
              </div>

              {/* Course codes within grouping (indented when expanded) */}
              {isExpanded &&
                grouping.courses.map((course) => (
                  <CourseNavListItem
                    className="ml-6"
                    course={course}
                    isSelected={!currentGrouping && currentCourse?.id === course.id}
                    key={course.id}
                  />
                ))}
            </div>
          );
        })}

        {/* Render ungrouped course codes */}
        {ungroupedCourses.map((course) => {
          return (
            <CourseNavListItem
              course={course}
              isSelected={!currentGrouping && currentCourse?.id === course.id}
              key={course.id}
            />
          );
        })}
      </div>

      {canEdit && (
        <>
          <SquareButton
            className={clsx("w-full", hasCourses && "mt-2")}
            eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course`}
            kind="tertiary"
            size="s"
            title={t("common.add.course")}
            onClick={() => showAddCourseModal()}
          >
            <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
          </SquareButton>

          <SquareButton
            className="mt-2 w-full"
            eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course Grouping`}
            kind="tertiary"
            size="s"
            title={t("common.add.course_grouping")}
            onClick={() => handleCreateCourseGrouping()}
          >
            <IconText iconStart={<FolderIcon />} text={t("common.add.course_grouping")} />
          </SquareButton>
        </>
      )}
    </div>
  );
};
